/**
 * A PDF's pages drawn on canvases, for a thumbnail and for the viewer.
 * Drawn here rather than framed, because a browser's own PDF viewer is
 * missing in some (Chrome on Android shows none in a frame). pdf.js is
 * loaded on the first call, so only a page that draws a PDF pays for it,
 * and its worker is a file of this origin, which the Content Security
 * Policy allows. The resumes use the standard fonts without embedding
 * them; pdf.js draws those with the system's own.
 */

type PdfJs = typeof import("pdfjs-dist");

let loading: Promise<PdfJs> | null = null;

function pdfjs(): Promise<PdfJs> {
    loading ??= Promise.all([
        import("pdfjs-dist"),
        import("pdfjs-dist/build/pdf.worker.min.mjs?url"),
    ]).then(([lib, worker]) => {
        lib.GlobalWorkerOptions.workerSrc = worker.default;
        return lib;
    });
    return loading;
}

/**
 * The first `most` pages of `pdf` (every page without it), each on a
 * canvas of its own `width` CSS pixels wide, sharp on the screen's own
 * pixel ratio. `signal` stops it, and a stopped draw answers with none.
 */
export async function drawPages(
    pdf: Blob,
    width: number,
    signal: AbortSignal,
    most = Infinity,
): Promise<HTMLCanvasElement[]> {
    const lib = await pdfjs();
    const data = new Uint8Array(await pdf.arrayBuffer());
    if (signal.aborted) return [];
    const task = lib.getDocument({ data });
    const onAbort = () => void task.destroy();
    signal.addEventListener("abort", onAbort, { once: true });
    try {
        const document = await task.promise;
        const ratio = window.devicePixelRatio || 1;
        const canvases: HTMLCanvasElement[] = [];
        for (let number = 1; number <= Math.min(document.numPages, most); number += 1) {
            const page = await document.getPage(number);
            const scale = (width * ratio) / page.getViewport({ scale: 1 }).width;
            const viewport = page.getViewport({ scale });
            const canvas = window.document.createElement("canvas");
            canvas.width = Math.floor(viewport.width);
            canvas.height = Math.floor(viewport.height);
            await page.render({ canvas, viewport }).promise;
            canvases.push(canvas);
        }
        return signal.aborted ? [] : canvases;
    } finally {
        signal.removeEventListener("abort", onAbort);
        await task.destroy();
    }
}
